/**
 * Add gateway form — first-time setup or adding additional gateways.
 *
 * Collects gateway URL, token, and optional name. On submit, creates
 * a new gateway config and initiates connection.
 *
 * Features:
 * - URL validation (must start with ws:// or wss://)
 * - Token required validation
 * - Loading state during connection
 * - Error display
 * - Cancel button (when adding additional gateways)
 */

import { useState, useCallback } from 'react';
import { reloadApp } from '../utils/reload';
import styles from './GatewaySetup.module.css';

interface GatewaySetupProps {
  /** Called when the user submits the form */
  onConnect: (url: string, token: string, name: string) => Promise<void>;
  /** Optional cancel handler (shown when adding additional gateways) */
  onCancel?: () => void;
  /** Error message to display */
  error?: string | null;
}

/**
 * Validate a gateway URL.
 *
 * @param url - The URL to validate
 * @returns Error message if invalid, null if valid
 */
function validateUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return 'URL is required';
  if (!trimmed.startsWith('ws://') && !trimmed.startsWith('wss://')) {
    return 'URL must start with ws:// or wss://';
  }
  try {
    new URL(trimmed.replace(/^ws/, 'http'));
  } catch {
    return 'Invalid URL format';
  }
  return null;
}

export function GatewaySetup({ onConnect, onCancel, error }: GatewaySetupProps) {
  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');
  const [name, setName] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setValidationError(null);

      // Validate URL
      const urlError = validateUrl(url);
      if (urlError) {
        setValidationError(urlError);
        return;
      }

      // Validate token
      if (!token.trim()) {
        setValidationError('Gateway token is required');
        return;
      }

      setIsConnecting(true);
      try {
        await onConnect(url.trim(), token.trim(), name.trim());
      } catch (err) {
        setValidationError(
          err instanceof Error ? err.message : 'Connection failed',
        );
      } finally {
        setIsConnecting(false);
      }
    },
    [url, token, name, onConnect],
  );

  const displayError = validationError || error;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>🔥 Talkyn</h1>
      <p className={styles.subtitle}>Connect to your OpenClaw Gateway</p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <input
          type="text"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setValidationError(null);
          }}
          placeholder="wss://your-gateway.ts.net"
          className={styles.input}
          autoComplete="url"
          spellCheck={false}
          required
          disabled={isConnecting}
        />
        <input
          type="password"
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
            setValidationError(null);
          }}
          placeholder="Gateway Token"
          className={styles.input}
          required
          disabled={isConnecting}
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name (optional)"
          className={styles.input}
          disabled={isConnecting}
        />

        <button
          type="submit"
          className={styles.primaryBtn}
          disabled={isConnecting || !url.trim() || !token.trim()}
        >
          {isConnecting ? 'Connecting…' : 'Connect'}
        </button>

        {displayError && <div className={styles.error}>{displayError}</div>}

        {onCancel && (
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onCancel}
            disabled={isConnecting}
          >
            Cancel
          </button>
        )}
      </form>

      <div style={{ marginTop: '30px', display: 'flex', gap: '12px' }}>
        <button
          type="button"
          className={styles.resetBtn}
          onClick={() => reloadApp()}
        >
          🔄 Reload
        </button>
        <button
          type="button"
          className={styles.resetBtn}
          onClick={() => {
            if (confirm('Clear all app data and reload?')) {
              localStorage.clear();
              reloadApp();
            }
          }}
        >
          🗑️ Reset App
        </button>
      </div>
    </div>
  );
}
